import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type {
  GarmentMovementDayDetail,
  GarmentMovementReport,
  RentalRevenue,
  RevenueGrain,
  StockSummary,
} from '@core/api/reports';
import {
  garmentMovementDayPath,
  garmentMovementPath,
  rentalRevenuePath,
  stockSummaryPath,
} from '@core/api/queryPaths';

export type {
  GarmentMovementDay,
  GarmentMovementDayDetail,
  GarmentMovementEntry,
  GarmentMovementReport,
  RentalRevenue,
  RevenueGrain,
  RevenueRow,
  StockSummary,
} from '@core/api/reports';

export const reportsKey = ['reports'] as const;

/** Report A1: what moved each shop day of the range. Off while the range would be refused. */
export function useGarmentMovement(
  branchId: number | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: [...reportsKey, 'garment-movement', { branchId, from, to }],
    queryFn: () => apiFetch<GarmentMovementReport>(garmentMovementPath(from, to)),
    enabled: enabled && branchId !== undefined,
  });
}

/**
 * Report B2: the branch's garments as they stand. `atCost` reads the value too, which needs
 * `Report:ViewFinancials` — the caller passes what the session holds, so nobody is offered a read
 * the server would refuse.
 */
export function useStockSummary(branchId: number | undefined, atCost: boolean) {
  return useQuery({
    queryKey: [...reportsKey, 'stock-summary', { branchId, atCost }],
    queryFn: () => apiFetch<StockSummary>(stockSummaryPath(atCost)),
    enabled: branchId !== undefined,
  });
}

/**
 * Report C2: what was earned over the range, a row per grain. Needs `Report:ViewFinancials`; the
 * page is offered only to a session holding it. Off while the range would be refused.
 */
export function useRentalRevenue(
  branchId: number | undefined,
  from: string,
  to: string,
  grain: RevenueGrain,
  enabled = true,
) {
  return useQuery({
    queryKey: [...reportsKey, 'rental-revenue', { branchId, from, to, grain }],
    queryFn: () => apiFetch<RentalRevenue>(rentalRevenuePath(from, to, grain)),
    enabled: enabled && branchId !== undefined,
  });
}

/** Report A1, one day opened: every garment that moved on it. */
export function useGarmentMovementDay(branchId: number | undefined, day: string | null) {
  return useQuery({
    queryKey: [...reportsKey, 'garment-movement', 'day', { branchId, day }],
    queryFn: () => apiFetch<GarmentMovementDayDetail>(garmentMovementDayPath(day ?? '')),
    enabled: branchId !== undefined && day !== null,
  });
}
